import GD from '../GD'
import { WSMessage } from '../enums'
import { Services, DCCAPI } from '../utils'
import { validateFileUploadCriteria } from './Validation'
import { Config } from '../managers'

/**
 * @author Veliyev Turan
**/

interface FileUploadData {
  file: File
  owner_uid?: string
  tempUID: string
}
export class ChunkUploader {
  private isThumbUpload = true
  private uploadData: FileUploadData | null = null
  private IMG_TYPES: string[] = ['image/', 'img/']
  private DEFAULT_CHUNK_SIZE = 300_000 // 300kb
  private thumbResponse: ChunkUploadResp | null = null
  private isAborted = false
  private chat: ChatVO

  async startUploadViaChunks(req: FileUploadData): Promise<boolean> {
    this.chat = await GD.GET_CHAT.request()
    this.uploadData = req
    return await this.processChunkUpload(req)
  }

  private async processChunkUpload(req: FileUploadData): Promise<boolean> {
    let file: File = req.file
    let success = true

    const isCriteriasPassed = await validateFileUploadCriteria({
      fileSize: file.size,
    })

    if(!isCriteriasPassed) {
      GD.ALERT_ERROR.invoke('Can\'t upload file: ' + req.file.name + ', size too big')
      success = false
      return success
    }

    const isImage = this.IMG_TYPES.some(IMG_TYPES => req.file.type.startsWith(IMG_TYPES))

    if (isImage && this.isThumbUpload) {
      file = await Services.imagePacker.resizeImage(file)
    }

    const fileName = file.name
    const totalSize = file.size

    // don't wait network resp, set initial progress
    GD.FILE_PROGRESS_UPDATE.invoke({ tempUID: req.tempUID, progress: 0 })

    const lastUploadedPosition = await GD.REQ_HTTP.request({
      method: 'files.getMyLastUploadedChunkPosition',
      chatUID: this.chat.uid,
      type: fileName.split('.').pop(),
      fileIndex: this.isThumbUpload ? 1 : 0,
      fileName: fileName,
      ver: isImage ? 1 : 5,
    })

    const initialPosition = lastUploadedPosition.data ?? 0

    if (initialPosition === totalSize) {
      console.error('File upload failed, ' + fileName)
      success = false
      return success
    }

    for (let start = initialPosition; start < totalSize; start += this.DEFAULT_CHUNK_SIZE) {
      if(this.isAborted) {
        this.isAborted = false
        return false
      }

      const end = Math.min(start + this.DEFAULT_CHUNK_SIZE, totalSize)
      const chunk = file.slice(start, end)

      const chunkArrayBuffer = await new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target?.result)
        reader.readAsArrayBuffer(chunk)
      })

      const { uid: thumb_rUID = null } = this.thumbResponse?.completionData || {}

      const chunkUploadResp = await this.sendChunkToServer(
        start, // position
        totalSize, // totalSize
        this.chat.uid,
        fileName.split('.').pop() as string, // type
        this.isThumbUpload ? 1 : 0, // fileIndex, 0 - full, 1 - thumb
        end - start, // chunk size
        fileName,
        false, // crypted
        isImage ? 1 : 5, // ver 0 - oldmethod full first, 1 - new method thumb first, 5 - file
        chunkArrayBuffer, // Pass the ArrayBuffer containing the chunk
        thumb_rUID ? thumb_rUID : undefined, // thumb uid to append full image into it
      )

      if (!chunkUploadResp || chunkUploadResp.error || chunkUploadResp.status === 'error') {
        console.error('Can\'t send file, ' + fileName)
        success = false
        return success
      }

      await this.finishChunkUpload(chunkUploadResp, isImage, req.tempUID)
    }

    return success
  }

  private async sendChunkToServer(
    position: number,
    totalSize: number,
    chatUID: string,
    type: string,
    fileIndex: number,
    chunkSize: number,
    fileName: string,
    crypted: boolean,
    ver: number,
    chunkData: any,
    thumb_rUID?: string,
  ) {
    const chunkBlob = new Blob([new Uint8Array(chunkData)], {
      type: 'application/octet-stream'
    })

    const response = await GD.REQ_HTTP.request({
      url: window.DCC_CONFIG.API_BASE_URL,
      method: 'files.uploadChunk',
      chunk: chunkBlob,
      position,
      totalSize,
      chatUID,
      type,
      fileIndex,
      chunkSize,
      fileName,
      crypted,
      ver,
      encrypt: false,
      ...(thumb_rUID ? { rUID: thumb_rUID } : {}),
    })

    return response.data
  }

  private async finishChunkUpload(chunkUploadResp: ChunkUploadResp, isImage: boolean, tempUID: string) {
    if (chunkUploadResp.status === 'progress') {      
      GD.FILE_PROGRESS_UPDATE.invoke({
        tempUID,
        progress: Math.round((chunkUploadResp.formedSize / chunkUploadResp.totalSize) * 100),
      })
    }

    if (chunkUploadResp.status === 'complete') {

      if (!isImage) {
        await this.sendFileUploadViaMSG(this.chat.uid, chunkUploadResp, this.chat.securityKey)
        this.resetValues()
      }

      else if (this.isThumbUpload && isImage) { // send thumb preview as msg
        await this.sendFakeThumbPreviewMsg(chunkUploadResp)
        await this.handleThumbnailUploadComplete(chunkUploadResp)
      }

      else if (!this.isThumbUpload && isImage) {
        await this.sendFileUploadViaMSG(this.chat.uid, chunkUploadResp, this.chat.securityKey)
        this.resetValues()
      }
    }
  }

  private async sendFakeThumbPreviewMsg(chunkUploadResp: ChunkUploadResp) {
    const profile = await GD.GET_PROFILE.request()

    GD.WS_MSG.invoke({
      method: WSMessage.MESSAGE_ADD_PREVIEW,
      data: {
        anonymous: false,
        chatUID: this.chat.uid,
        chat_uid: this.chat.uid,
        created: Math.round((+new Date() / 1000)),
        delivery: 'sent',
        id: '',
        mid: '-1',
        num: Infinity,
        platform: 'desktop',
        stat: [],
        status: 'uploading',
        user_avatar: null,
        user_name: profile?.name,
        user_uid: profile?.uid,
        mine: true,
        side: 'right',
        text: DCCAPI.pack(
          Config.systemSequence +
          JSON.stringify({
            mine: true,
            side: 'left',
            method: 'fileSended',
            title: this.uploadData?.file.name,
            type: 'file',
            fileType: 'cimg',
            additionalData: chunkUploadResp.completionData.uid,
            created: chunkUploadResp.completionData.ctime_upl,
            message: {
              side: 'right',
              mine: true,
              created: chunkUploadResp.completionData.ctime_upl,
              text: '',
              type: 'file',
              fileData: chunkUploadResp.completionData,
              title: this.uploadData?.file.name,
              uid: chunkUploadResp.completionData.uid,
              fileType: 'cimg'
            }
          }),
          this.chat.securityKey,
        ),
      }
    })
  }

  private async handleThumbnailUploadComplete(chunkUploadResp: ChunkUploadResp) {
    GD.FILE_PROGRESS_DELAY.invoke({ delay: true })
    this.isThumbUpload = false
    this.thumbResponse = chunkUploadResp
    await this.startUploadViaChunks(this.uploadData as FileUploadData) // upload fullsize to attach on thumbnail
  }

  private resetValues() {
    this.isThumbUpload = true
    this.thumbResponse = null
    this.uploadData = null
    GD.FILE_PROGRESS_DELAY.invoke({ delay: false })
  }

  async sendFileUploadViaMSG(chatUID: string, uploadedFileData: any, secKey: string) {
    const { completionData = null } = uploadedFileData
    if (!completionData) return

    const isImage = this.IMG_TYPES.some(type => completionData.type.startsWith(type))

    GD.WS_SEND.invoke({
      method: WSMessage.MESSAGE_ADD,
      data: {
        chatUID: chatUID,
        text: DCCAPI.pack(
          Config.systemSequence +
          JSON.stringify({
            method: 'fileSended',
            title: completionData.name,
            type: 'file',
            fileType: isImage ? 'cimg' : 'file',
            fileData: isImage
              ? completionData.additionalData
              : completionData,
            additionalData: completionData.uid
          }),
          secKey
        )
      }
    })
  }
}
export default ChunkUploader